import React, { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { InView } from 'react-intersection-observer'
import {
  AddToTrolley,
  AddToWishlist,
  Image,
  SpecCardSkeleton,
  ProductCardSkeleton,
  ErrorBoundary
} from '@sainsburys-tech/bolt'
import { TuLogo } from '@sainsburys-tech/bolt-icons'

import Templates from './ProductList.template'

import useAsync from '../../hooks/useAsync'
import { getValue } from '../../utils/ConfigProvider'
import { trackWishlist } from '../../helpers/TaggingSerializer'
import { availabilityIsEnabled } from '../../utils/availabilityIsEnabled'
import { formatAvailabilityMessages } from '../../helpers/AvailabilityHelper'

import * as Styled from './styles.js'

const ProductListCard = ({
  attributes,
  citrus,
  className,
  currentPage,
  index,
  isCannedSearch,
  isClearance,
  layout,
  localisationPostcode,
  numberOfResults,
  pageSize,
  pageType,
  partNumber,
  productCardOnClick,
  relavancyRecallOffset,
  searchTerm,
  sendCitrusClickBeacon,
  sendCitrusImpressionBeacon,
  showLoader,
  templateType,
  userState,
  whiteGoodCategory,
  allCookies,
  location
}) => {
  const Skeleton = () => (
    <Styled.SkeletonWrapper className={className}>
      <ProductCardSkeleton isPortrait={layout === Templates.clothingLayout} mobileLayout='vertical' />
    </Styled.SkeletonWrapper>
  )
  const SpecSkeleton = () => (
    <Styled.SkeletonWrapper whiteGoods className={className}>
      <SpecCardSkeleton />
    </Styled.SkeletonWrapper>
  )

  const ProductCard = loadable(() => import(/* webpackChunkName: "labs-product-card" */ './StyledProductCard.js'), {
    fallback: <Skeleton />
  })

  const SpecCard = loadable(() => import(/* webpackChunkName: "spec-card" */ './SpecCard.js'), {
    fallback: <SpecSkeleton />
  })

  const buildQueryParamTracking = relevancyRank => {
    const position = pageSize * (currentPage - 1) + (index + 1)
    return searchTerm
      ? `/product/${partNumber}?clickSR=slp:term:${searchTerm}:${position}:${numberOfResults}:${relevancyRank}`
      : `/product/${partNumber}?clickPR=${!isClearance ? 'plp' : 'clearance'}:${position}:${numberOfResults}`
  }

  const getBrandLogo = brand => {
    // Get the opt-in feature status
    const additionalBrandLogos = getValue('features.productList.additionalBrandLogos')

    // If the feature value is an array, find a matching logo for the brand.
    if (Array.isArray(additionalBrandLogos)) {
      // The brand name may have different casing to our data, so normalise while searching.
      const brandLogo = additionalBrandLogos.find(
        ({ name }) => String(brand).toLowerCase() === String(name).toLowerCase()
      )

      // If there's a brand in the additionalBrandLogos, and it's got a logo URL, return a BrandLogo
      if (brandLogo && brandLogo.logoUrl) {
        return <Image alt={brand} src={encodeURI(brandLogo.logoUrl)} height={20} />
      }
    }

    // For clothing layouts, apply custom brand logo logic
    if (layout === Templates.clothingLayout) {
      // Display brands that aren't Tu as their brand name
      if (brand !== 'Tu') {
        return brand
      }

      // Don't show the Tu logo for Tu brand products, if it's disabled in features
      if (!getValue('features.productList.disableTuLogo')) {
        return <TuLogo height={24} width={24} />
      }
    }

    // If this is a Habitat browse page, give every product the Habitat logo
    if (pageType === 'browse' && brand === 'Habitat') {
      return <Styled.HabitatLogo height={26} width={60} />
    }

    // Return no brand logo for all other scenarios
    return null
  }

  const populateSpecificationData = () => {
    return {
      heading: Templates.specificationHeader,
      secondaryData: Templates.secondaryDataArray(attributes),
      headerLink: (
        <>
          <a href={`/product/${attributes.partNumber}`}>{Templates.headerText}</a>
        </>
      ),
      overviewData: { content: attributes.detailAttributes },
      smallTitle: true
    }
  }

  const isExcluded = (isDeliverable, isReservable, hasVariations) => {
    return !isDeliverable && !isReservable && !hasVariations
  }

  const isClothing = partNumber.startsWith('tuc')

  const showSkeleton = () => {
    return showLoader || typeof attributes.price !== 'number'
  }

  const isLazyLoadImage = position => {
    return position > 6
  }

  const alternativeImagesConditionChecker = () => {
    if (getValue('features.productList.forceEnableAltImageHover')) {
      return true
    }

    return (
      templateType && templateType.toLowerCase() !== Templates.defaultTemplate && pageType === Templates.pageTypeBrowse
    )
  }

  const handleAddToTrolleyClick = (event, productInfo, positionInList, clickOrigin) =>
    AddToTrolley.onAddToTrolleyModalOpen(productInfo)(clickOrigin, positionInList)()

  const trolleyOnClick = event => {
    const productInfo = [{ id: partNumber, price: attributes.price.toString(), quantity: 1 }]
    const clickOrigin =
      pageType === Templates.pageTypeBrowse ? Templates.browseAddToTrolleyOrigin : Templates.searchAddToTrolleyOrigin
    handleAddToTrolleyClick(event, productInfo, index + 1, clickOrigin)
    sendCitrusClickBeacon(attributes, citrus, partNumber)
  }

  const productInfoOnly = isExcluded(attributes.deliverable, attributes.reservable, attributes.hasVariations)
  const colorVariants = attributes.variants && attributes.variants.Colour

  let recallPosition = relavancyRecallOffset + index
  recallPosition = !attributes.featured ? (recallPosition += 1) : recallPosition
  const isLazy = isLazyLoadImage(recallPosition)
  const imageVariant = alternativeImagesConditionChecker(templateType, pageType)

  if (attributes.featured) {
    sendCitrusImpressionBeacon(attributes, citrus)
  }

  const availabilityEnabled = availabilityIsEnabled({
    cookieVariant: allCookies && allCookies.Findability_Test_Group_1,
    pageType,
    isClearance,
    isClothing,
    isWhiteGoods: whiteGoodCategory,
    urlPath: location?.pathname
  })

  const [availabilityLabels, setAvailabilityLabels] = useState([])
  const { status, run, complete } = useAsync()

  const fetchProductAvailability = async () => {
    const url = `/stores/api/orchestrator/v0/locator/availability?maxDistance=10&maxResults=5&skuQty=${partNumber}_1&channel=web_plp&postcode=${localisationPostcode}&origin=${localisationPostcode}&storeSort=TA`

    const response = await fetch(url)

    if (response.ok) {
      const data = await response.json()

      if (data) {
        const availability = {
          delivery: Object.values(data?.delivery[0]?.messages)[0],
          collection: data?.stores.map(store => Object.values(store?.messages)[0])
        }

        setAvailabilityLabels(formatAvailabilityMessages(availability))
        return data
      }

      return Promise.reject(new Error(`Availability for SKU ${partNumber} cannot be fetched`))
    }

    // network errors etc
    return Promise.reject(response)
  }

  const fetchAvailability = () => {
    if (availabilityEnabled && localisationPostcode && partNumber !== undefined) {
      run(fetchProductAvailability())
    }
  }

  const onProductCardView = inView => {
    if (inView && isLazy && !complete) {
      fetchAvailability()
    }
  }

  useEffect(() => {
    !isLazy && fetchAvailability()
  }, [localisationPostcode])

  return !whiteGoodCategory ? (
    !showSkeleton(showLoader, attributes) ? (
      <Styled.LazyHydrateCard
        key={`${partNumber}${index}`}
        whenVisible
        style={{ display: 'flex' }}
        className={className}>
        <InView
          style={{ display: 'flex', width: '100%' }}
          skip={!isLazy}
          onChange={onProductCardView}
          rootMargin='250px 0px'>
          <ProductCard
            onClick={productCardOnClick}
            lazyImageLoad={isLazy}
            isPortrait={layout === Templates.clothingLayout}
            mobileLayout='vertical'
            partNumber={partNumber}
            imageHoverActive={imageVariant}
            colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
            brand={getBrandLogo(attributes.brand, layout, pageType)}
            featured={attributes.featured} // TODO: FEATURED
            name={attributes.name}
            price={attributes.price}
            wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
            specialOfferText={attributes.specialOfferText}
            rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
            reviewsCount={attributes.reviewsCount}
            productLinkOverride={
              !attributes.productLink && !isCannedSearch
                ? buildQueryParamTracking(attributes.relevancyRank)
                : attributes.productLink
            }
            badge={attributes.badge}
            freeDelivery={attributes.freeDelivery}
            hasVariations={attributes.hasVariations}
            hasMultiplePrices={attributes.hasMultiplePrices}
            productInfoOnly={productInfoOnly}
            buttonLabel={attributes.hasVariations && (productInfoOnly ? 'More Details' : 'Choose Options')}
            turnOffCta={getValue('features.productCard.turnOffCta')}
            cta={
              <ErrorBoundary>
                <AddToTrolley.Button
                  className='btn-cta'
                  partNumber={partNumber}
                  openView
                  showIcon
                  onClick={trolleyOnClick}
                />
              </ErrorBoundary>
            }
            wishlistCta={
              getValue('features.wishlist.enabled') && (
                <ErrorBoundary>
                  <AddToWishlist.Button
                    item={{
                      partNumber,
                      hasVariant: attributes.hasVariations
                    }}
                    tooltipDurationToShow={10000}
                    tooltipWidth={260}
                    onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                    tooltipPosition='right'
                  />
                </ErrorBoundary>
              )
            }
            colorSwatchInline={getValue('features.productCard.colorSwatchInline')}
            colorSwatchAlwaysVisible={getValue('features.productCard.colorSwatchAlwaysVisible')}
            colorSwatchMaxVariations={getValue('features.productCard.colorSwatchMaxVariations')}
            colorSwatchSize={getValue('features.productCard.colorSwatchSize')}
            availability={
              availabilityEnabled && localisationPostcode
                ? {
                    loading: status === 'pending',
                    availabilityLabels
                  }
                : null
            }
          />
        </InView>
      </Styled.LazyHydrateCard>
    ) : (
      <Skeleton />
    )
  ) : !showSkeleton(showLoader, attributes) ? (
    <Styled.WhiteGoodsWrapper className={className}>
      <Styled.LazyHydrateCard whenVisible>
        <SpecCard
          key={`${partNumber}${index}`}
          isPortrait={layout === Templates.clothingLayout}
          partNumber={partNumber}
          imageHoverActive={imageVariant}
          colorVariants={!!colorVariants && colorVariants.length > 1 && colorVariants}
          brand={getBrandLogo(attributes.brand, layout, pageType)}
          featured={attributes.featured} // TODO: FEATURED
          name={attributes.name}
          price={attributes.price}
          wasPrice={!!attributes.wasPrice && attributes.wasPrice > 0 && attributes.wasPrice}
          specialOfferText={attributes.specialOfferText}
          rating={!!attributes.avgRating && attributes.avgRating > 0 && attributes.avgRating}
          reviewsCount={attributes.reviewsCount}
          productLinkOverride={
            !attributes.productLink && !isCannedSearch
              ? buildQueryParamTracking(attributes.relevancyRank)
              : attributes.productLink
          }
          badge={attributes.badge}
          freeDelivery={attributes.freeDelivery}
          hasVariations={attributes.hasVariations}
          hasMultiplePrices={attributes.hasMultiplePrices}
          productInfoOnly={productInfoOnly}
          buttonLabel={productInfoOnly ? 'More Details' : 'Choose Options'}
          cta={
            !getValue('features.productCard.turnOffCta') && (
              <ErrorBoundary>
                <AddToTrolley.Button className='btn-cta' partNumber={partNumber} openView showIcon />
              </ErrorBoundary>
            )
          }
          wishlistCta={
            <ErrorBoundary>
              <AddToWishlist.Button
                item={{
                  partNumber,
                  hasVariant: attributes.hasVariations
                }}
                tooltipDurationToShow={10000}
                tooltipWidth={260}
                onUpdate={isChecked => trackWishlist(isChecked, partNumber, userState)}
                tooltipPosition='left'
              />
            </ErrorBoundary>
          }
          specificationData={populateSpecificationData(attributes)}
        />
      </Styled.LazyHydrateCard>
    </Styled.WhiteGoodsWrapper>
  ) : (
    <SpecSkeleton />
  )
}

export default ProductListCard
